import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import srOnly from 'styles/tools/sr-only'
import Clickable from 'components/Common/Clickable'
import MapViewBand from 'components/Common/MapViewBand'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { ChatExploreCategory, ChatExploreCategoryKey } from 'tripPlanner/config/exploreCategories'
import { englishExploreCategoryLabel } from 'tripPlanner/config/exploreCategories'
import type { ExploreItem } from 'tripPlanner/types/common'
import type { GeoPoint } from 'tripPlanner/types/placeAndDateTime'
import { buildThingsToDoCategorySelectedActivityEntry } from '../../containers/buildThingsToDoCategorySelectedActivityEntry'
import ThingsToDoCategoryChips from './ThingsToDoCategoryChips'
import ThingsToDoExperiencesRow from './ThingsToDoExperiencesRow'
import ThingsToDoPlacesResults from './ThingsToDoPlacesResults'
import thingsToDoNotices, { activeThingsToDoNotice } from './thingsToDoNotices'
import { THINGS_TO_DO_DEFAULT_CATEGORY } from './thingsToDoCategories'
import useThingsToDoPlaces from './useThingsToDoPlaces'

/* A region that is only mounted when it already holds its text is not reliably announced. */
const LiveRegion = styled.div`
  ${srOnly}
`

interface Props {
  destinationName: string
  destinationQueryText: string
  coordinates?: GeoPoint
  onSelectPlace: (place: ExploreItem) => void
  onViewOnMap?: (categoryKey: ChatExploreCategoryKey) => void
  experiencesSummary?: { count: number; formattedLowestPrice?: string }
  onViewExperiences?: () => void
  maxPlaces?: number
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAction?: () => void
}

function ThingsToDoPlacesTab({
  destinationName,
  destinationQueryText,
  coordinates,
  onSelectPlace,
  onViewOnMap,
  experiencesSummary,
  onViewExperiences,
  maxPlaces,
  itineraryData,
  onSendMessage,
  onAfterAction = noop,
}: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBusOptional()
  const [selectedCategory, setSelectedCategory] = useState<ChatExploreCategory>(THINGS_TO_DO_DEFAULT_CATEGORY)

  const { places, isLoading, isFetching, isError, isPartial, isShowingNearest, refetch } = useThingsToDoPlaces({
    destinationQueryText,
    coordinates,
    categoryKey: selectedCategory.key,
  })

  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  /* Reported where the selection is accepted, so the assistant sees the category whether or not the
     traveller goes on to the map. Re-selecting the lit chip changes nothing and is not an action. */
  const handleSelectCategory = useCallback((category: ChatExploreCategory) => {
    if (category.key === selectedCategory.key) return
    setSelectedCategory(category)
    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildThingsToDoCategorySelectedActivityEntry(
        englishExploreCategoryLabel(category.key),
        destinationName,
      )],
    })
  }, [destinationName, eventBus, selectedCategory.key])

  const handleViewOnMap = useCallback(() => {
    onViewOnMap?.(selectedCategory.key)
  }, [onViewOnMap, selectedCategory.key])

  const visiblePlaces = useMemo(
    () => (maxPlaces === undefined ? places : places.slice(0, maxPlaces)),
    [maxPlaces, places],
  )

  const categoryLabel = intl.formatMessage(selectedCategory.labelMessage)

  const noticeAnnouncement = useMemo(() => {
    const notice = activeThingsToDoNotice({
      isLoading,
      isError,
      isPartial,
      isShowingNearest,
      placeCount: visiblePlaces.length,
    })
    if (!notice) return ''

    return intl.formatMessage(notice, { category: categoryLabel, destination: destinationName })
  }, [categoryLabel, destinationName, intl, isError, isLoading, isPartial, isShowingNearest, visiblePlaces.length])

  return (
    <Group direction="vertical" gap={16}>
      <LiveRegion aria-live="polite" aria-atomic="true">{noticeAnnouncement}</LiveRegion>
      {onViewOnMap && (
        <Clickable onClick={handleViewOnMap}>
          <MapViewBand isNonInteractable />
        </Clickable>
      )}

      {experiencesSummary && onViewExperiences && (
        <ThingsToDoExperiencesRow
          count={experiencesSummary.count}
          formattedLowestPrice={experiencesSummary.formattedLowestPrice}
          onClick={onViewExperiences}
        />
      )}

      <ThingsToDoCategoryChips
        label={intl.formatMessage({
          id: 'Ps24jYXQSpItb5UOwtv8',
          defaultMessage: 'Filter things to do by category',
          description: 'Accessible name for the row of category filter chips in the trip planner things to do drawer',
        })}
        selectedCategoryKey={selectedCategory.key}
        onSelectCategory={handleSelectCategory}
        isFetching={isFetching}
      />

      {isShowingNearest && (
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage(thingsToDoNotices.nearest, { category: categoryLabel, destination: destinationName })}
        </BodyText>
      )}

      {isPartial && (
        <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap">
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage(thingsToDoNotices.partial)}
          </BodyText>
          <TextButton kind="tertiary" size="small" onClick={handleRetry}>
            {intl.formatMessage({
              id: 'q2nIV5-Sco41UZtxNQNg',
              defaultMessage: 'Try again',
              description: 'Button label to retry loading hotel offer details after an error',
            })}
          </TextButton>
        </Group>
      )}

      {isError && (
        <Group direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage(thingsToDoNotices.error)}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={handleRetry}>
            {intl.formatMessage({
              id: 'q2nIV5-Sco41UZtxNQNg',
              defaultMessage: 'Try again',
              description: 'Button label to retry loading hotel offer details after an error',
            })}
          </TextButton>
        </Group>
      )}

      {!isError && (
        <ThingsToDoPlacesResults
          places={visiblePlaces}
          isLoading={isLoading}
          isPartial={isPartial}
          skeletonCount={maxPlaces}
          destinationName={destinationName}
          categoryLabel={categoryLabel}
          onSelectPlace={onSelectPlace}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
          onAfterAction={onAfterAction}
        />
      )}
    </Group>
  )
}

export default ThingsToDoPlacesTab
